import {
  Input,
  List,
  message,
  PageHeader,
  Pagination,
  Skeleton,
  Typography,
} from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SurveyCampaignStatusTag from "../../components/SurveyCampaignStatusTag";
import {
  GetSurveyCampaignBySurveyCampaignIdRequest,
  GetSurveyCampaignsRequest,
  GetSurveyCampaignsResponse,
  GetSurveyTemplatesBySurveyTemplateIdRequest,
} from "../../constants/apiRequestResponse";
import { SurveyTemplate } from "../../constants/types";
import {
  getSurveyCampaignBySurveyCampaignId,
  getSurveyCampaigns,
  getSurveyResponsesBySurveyCampaignId,
  getSurveyTemplateBySurveyTemplateId,
  getSurveyTemplates,
} from "../../services/api";
import { ReportsPageKeys } from "../../services/i8tn/ReportsPage/keys";
import { SurveyCampaignKeys } from "../../services/i8tn/SurveyCampaignsPage/keys";
import {
  getQueryParam,
  parseTimeAgo,
  setAllQueryParams,
} from "../../util/index";
import styles from "./SurveyCampaignPage.module.css";
import SurveyCampaignView from "./SurveyCampaignView";

export default function SurveyCampaignsPage() {
  const { t } = useTranslation();
  const [surveyTemplates, setSurveyTemplates] = useState<SurveyTemplate[]>([]);
  const [
    getSurveyCampaignsResponse,
    setSurveyCampaignsResponse,
  ] = useState<GetSurveyCampaignsResponse | undefined>();
  const [req, setReq] = useState<GetSurveyCampaignsRequest>({
    limit: Number(getQueryParam("limit")) || 10,
    skip: Number(getQueryParam("skip")) || 0,
    search: getQueryParam("search") || "",
  });
  const [isSearchLoading, setSearchLoading] = useState(false);
  const [
    isGetSurveyCampaignsByCompanyIdLoading,
    setIsGetSurveyCampaignsByCompanyIdLoading,
  ] = useState(false);

  const getData = useCallback(
    (req: GetSurveyCampaignsRequest) => {
      setIsGetSurveyCampaignsByCompanyIdLoading(true);
      getSurveyCampaigns(req)
        .then((res) => {
          if (!res.success) throw new Error(res.message);

          setSurveyCampaignsResponse(res);
        })
        .catch((err) => {
          message.error(err.message);
        })
        .finally(() => setIsGetSurveyCampaignsByCompanyIdLoading(false));

      if (surveyTemplates.length === 0) {
        getSurveyTemplates()
          .then((res) => {
            if (!res.success) throw new Error(res.message);
            setSurveyTemplates(res.surveyTemplates);
          })
          .catch((err) => {
            message.error(err.message);
          });
      }
    },
    [surveyTemplates.length]
  );

  useEffect(() => {
    getData(req);
  }, [getData, req]);

  const dataSource = getSurveyCampaignsResponse?.surveyCampaigns.map(
    (sc) => {
      return {
        ...sc,
        surveyTemplateName: surveyTemplates.find(
          (st) => st._id === sc.surveyTemplateId
        )?.name,
      };
    }
  );

  return (
    <div className="page-content">
      <PageHeader
        title={t(SurveyCampaignKeys.surveyCampaigns_surveyCampaignsHeader)}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          justifyContent: "space-between",
          gap: 20,
        }}
      >
        <div
          className={styles.card}
          style={{
            width: "30%",
            overflow: "scroll",
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
          }}
        >
          <div>
            <Input.Search
              loading={isSearchLoading}
              placeholder={t(SurveyCampaignKeys.surveyCampaigns_searchPlaceholder)}
              defaultValue={req.search}
              style={{ marginBottom: 10 }}
              onSearch={(searchString) => {
                const newReq = {
                  ...req,
                  skip: 0,
                  search: searchString,
                };

                setReq(newReq);
                setAllQueryParams(newReq);

                setSearchLoading(true);
                getSurveyCampaigns(newReq)
                  .then((res) => {
                    if (!res.success) throw new Error(res.message);

                    setSurveyCampaignsResponse(res);
                  })
                  .catch((err) => {
                    message.error(err.toString());
                  })
                  .finally(() => setSearchLoading(false));
              }}
            />

            {isGetSurveyCampaignsByCompanyIdLoading ? (
              <Skeleton active />
            ) : (
              <List
                dataSource={dataSource}
                renderItem={(row) => {
                  return (
                    <Link
                      onMouseOver={() => {
                        // prefetch to cache at network layer
                        const req1: GetSurveyCampaignBySurveyCampaignIdRequest = {
                          surveyCampaignId: row._id,
                        };
                        getSurveyCampaignBySurveyCampaignId(req1);

                        const req2: GetSurveyTemplatesBySurveyTemplateIdRequest = {
                          surveyTemplateId: row.surveyTemplateId,
                        };
                        getSurveyTemplateBySurveyTemplateId(req2);

                        const req3: GetSurveyCampaignBySurveyCampaignIdRequest = {
                          surveyCampaignId: row._id,
                        };
                        getSurveyResponsesBySurveyCampaignId(req3)

                      }}
                      to={`/surveycampaign?surveyCampaignId=${row._id}`}>
                      <List.Item key={row._id}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                display: "flex",
                                gap: 5,
                              }}
                            >
                              <Typography.Paragraph>
                                {row.name || t(SurveyCampaignKeys.surveyCampaigns_noNameProvidedString)}
                              </Typography.Paragraph>
                              <SurveyCampaignStatusTag status={row.status} />
                            </div>

                            <div>
                              <p style={{ color: "grey" }}>
                                {row.surveyTemplateName}
                              </p>
                              <p
                                style={{
                                  color: "grey",
                                }}
                              >
                                {t(
                                  ReportsPageKeys.reportsPage_lastUpdateString
                                )}
                                {parseTimeAgo(row.createdAt)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </List.Item>
                    </Link>
                  );
                }}
              />
            )}
          </div>
          <Pagination
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "10px 0px",
            }}
            total={
              getSurveyCampaignsResponse
                ? getSurveyCampaignsResponse.total
                : 10
            }
            defaultCurrent={
              req?.skip && req?.limit ? req.skip / req.limit + 1 : 1
            }
            defaultPageSize={req.limit}
            onChange={(page, pageSize) => {
              const newReq = {
                ...req,
                limit: pageSize,
                skip: (page - 1) * (pageSize ? pageSize : 0),
              };
              setAllQueryParams(newReq);
              setReq(newReq);

              setSearchLoading(true);
              getSurveyCampaigns(newReq)
                .then((res) => {
                  if (!res.success) throw new Error(res.message);

                  setSurveyCampaignsResponse(res);
                })
                .catch((err) => {
                  message.error(err.toString());
                })
                .finally(() => setSearchLoading(false));
            }}
          />
        </div>

        <div
          className={styles.card}
          style={{
            width: "70%",
            overflow: "scroll",
          }}
        >
          <SurveyCampaignView
            onSurveyCampaignUpdated={(updatedSurveyCampaign) => {
              setSurveyCampaignsResponse((prev) => ({
                ...prev,
                success: true,
                surveyTemplates: getSurveyCampaignsResponse?.surveyTemplates || [],
                // replace survey campaign with the updated one
                surveyCampaigns: getSurveyCampaignsResponse?.surveyCampaigns.map((sc) => {
                  if (sc._id === updatedSurveyCampaign._id) {
                    return updatedSurveyCampaign
                  }
                  return sc;
                }) || [],
              }));
            }}
          />
        </div>
      </div>
    </div>
  );
}
